// ProxyCard.jsx
import React, { useState, useEffect, useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import { formatDate, getProxyStatusColor } from '../../utils/format';
import { proxiesAPI } from '../../api/proxies';

/**
 * Вспомогательная функция для сравнения changedFields (массив строк)
 */
const compareChangedFields = (prev, next) => {
  if (!prev && !next) return true;
  if (!prev || !next) return false;
  if (prev.length !== next.length) return false;
  
  const prevSet = new Set(prev);
  return next.every(field => prevSet.has(field));
};

/**
 * Компонент для анимированного отображения значения с подсветкой при изменении
 */
const HighlightedValue = ({ children, fieldName, value, className = '' }) => {
  return (
    <motion.span
      key={`${fieldName}-${String(value)}`} // Триггер анимации при смене значения
      className={`inline-block ${className}`}
      initial={{ backgroundColor: '#fef08a', scale: 1.03 }}
      animate={{ backgroundColor: 'transparent', scale: 1 }}
      transition={{ duration: 1.2, ease: 'easeOut' }}
    >
      {children}
    </motion.span>
  );
};

const ProxyCard = ({ proxy, index = 0, changedFields = [], update }) => {
  const { t } = useTranslation();
  const [pingResult, setPingResult] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [pingProgress, setPingProgress] = useState({ current: 0, total: 5 });
  
  // Локальное состояние для плавного обновления данных
  const [localProxy, setLocalProxy] = useState(proxy);
  const proxyIdRef = useRef(proxy?.proxyId);

  // Синхронизация локального состояния с пропсами
  useEffect(() => {
    if (!proxy?.proxyId) return;
    
    // Если прокси сменился (другой ID) — сбрасываем состояние
    if (proxy.proxyId !== proxyIdRef.current) {
      proxyIdRef.current = proxy.proxyId;
      setLocalProxy(proxy);
      setPingResult(null);
      setPingProgress({ current: 0, total: 5 });
      return;
    }
    
    // Обновляем только изменённые поля
    setLocalProxy(prev => ({ ...prev, ...proxy }));
    
  }, [proxy]);

  // Обработчик ping
  const handlePing = async () => {
    if (pinging || !localProxy?.isOnline) return;
    
    setPinging(true);
    setPingResult(null);
    setPingProgress({ current: 0, total: 5 });
    
    try {
      const result = await proxiesAPI.pingProxy(localProxy, {
        count: 5,
        onProgress: (current, total) => {
          setPingProgress({ current, total });
        }
      });
      setPingResult(result);
    } catch (error) {
      setPingResult({ success: false, latency: null, error: error.message });
    } finally {
      setPinging(false);
    }
  };

  // Утилиты для отображения
  const getLatencyColor = (latency) => {
    if (latency == null) return 'text-gray-400';
    if (latency < 100) return 'text-green-600';
    if (latency < 200) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getLatencyVariant = (latency) => {
    if (latency == null) return 'default';
    if (latency < 100) return 'success';
    if (latency < 200) return 'warning';
    return 'error';
  };

  const getStatusText = (status) => t(`proxies.status.${status}`) || status;

  // Перевод ошибок пинга
  const getPingErrorTranslation = (error) => {
    if (error === 'Ping port not configured') {
      return t('proxies.card.errors.pingPortNotConfigured');
    }
    if (error === 'No host address available') {
      return t('proxies.card.errors.noHostAddress');
    }
    return error;
  };

  // Анимационные варианты
  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.97 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        duration: 0.25, 
        ease: 'easeOut',
        delay: index * 0.03 // Каскадная загрузка списка
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: -20,
      transition: { duration: 0.2, ease: 'easeIn' }
    }
  };

  // Пропуск рендера если нет данных
  if (!localProxy?.proxyId) return null;

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout // FLIP-анимация для плавного перестроения списка
      layoutId={`proxy-card-${localProxy.proxyId}`} // Cross-fade при обновлении контента
      transition={{ layout: { duration: 0.25, ease: 'easeInOut' } }}
      className="h-full"
    >
      <Card className="hover:shadow-lg transition-shadow h-full">
        
        {/* Заголовок карточки */}
        <motion.div className="flex items-start justify-between mb-4" layout>
          <div className="flex items-center min-w-0">
            <motion.svg 
              className="h-8 w-8 text-blue-600 mr-3 flex-shrink-0" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              layout
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" 
              />
            </motion.svg>
            
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                <HighlightedValue 
                  fieldName="name" 
                  value={localProxy.name || localProxy.proxyId}
                >
                  {localProxy.countryCode ? `${localProxy.countryCode} - ` : ''}
                  {localProxy.name || localProxy.proxyId}
                </HighlightedValue>
              </h3>
              <p className="text-sm text-gray-500 font-mono truncate" title={localProxy.proxyId}>
                {localProxy.proxyId}
              </p>
            </div>
          </div>
          
          <HighlightedValue fieldName="status" value={localProxy.status}>
            <Badge variant={getProxyStatusColor(localProxy.status)}>
              {getStatusText(localProxy.status)}
            </Badge>
          </HighlightedValue>
        </motion.div>

        {/* Основная информация */}
        <motion.div className="space-y-2 text-sm" layout>
          {[
            { label: 'proxies.card.country', field: 'countryName', value: localProxy.countryName },
            { label: 'proxies.card.region', field: 'regionName', value: localProxy.regionName },
            { label: 'proxies.card.city', field: 'cityName', value: localProxy.cityName },
          ].map(({ label, field, value }) => value && (
            <div key={field} className="flex justify-between">
              <span className="text-gray-600">{t(label)}</span>
              <HighlightedValue fieldName={field} value={value}>
                <span className="text-gray-900 font-medium">{value}</span>
              </HighlightedValue>
            </div>
          ))}

          {(localProxy.ipAddress || localProxy.domain) && (
            <div className="flex justify-between">
              <span className="text-gray-600">{t('proxies.card.address')}</span>
              <HighlightedValue fieldName="address" value={localProxy.domain || localProxy.ipAddress}>
                <span className="text-gray-900 font-mono text-xs break-all">
                  {localProxy.domain || localProxy.ipAddress}
                </span>
              </HighlightedValue>
            </div>
          )}

          {localProxy.port && (
            <div className="flex justify-between">
              <span className="text-gray-600">{t('proxies.card.port')}</span>
              <HighlightedValue fieldName="port" value={localProxy.port}>
                <span className="text-gray-900 font-mono">{localProxy.port}</span>
              </HighlightedValue>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-600">{t('proxies.card.lastHeartbeat')}</span>
            <HighlightedValue fieldName="lastHeartbeat" value={localProxy.lastHeartbeat}>
              <motion.span
                className="text-gray-900"
                key={localProxy.lastHeartbeat}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                {localProxy.lastHeartbeat ? formatDate(localProxy.lastHeartbeat) : '—'}
              </motion.span>
            </HighlightedValue>
          </div>
        </motion.div>

        {/* Секция Ping */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center flex-wrap gap-2 min-w-0">
              <AnimatePresence mode="wait">
                {pingResult?.success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    className="flex items-center gap-2 flex-wrap"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('proxies.card.latency')}:</span>
                      <motion.span
                        className={`text-sm font-semibold ${getLatencyColor(pingResult.latency)}`}
                        key={pingResult.latency}
                        initial={{ scale: 1.2 }}
                        animate={{ scale: 1 }}
                      >
                        {pingResult.latency} ms
                      </motion.span>
                      <Badge variant={getLatencyVariant(pingResult.latency)} className="text-xs flex-shrink-0">
                        {pingResult.latency < 100
                          ? t('proxies.card.latencyExcellent')
                          : pingResult.latency < 200
                            ? t('proxies.card.latencyGood')
                            : t('proxies.card.latencyPoor')}
                      </Badge>
                    </div>
                    
                    {/* Дополнительная статистика */}
                    {pingResult.avgLatency && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <span>min: {pingResult.latency}ms</span>
                        <span>•</span>
                        <span>avg: {pingResult.avgLatency}ms</span>
                        <span>•</span>
                        <span>max: {pingResult.maxLatency}ms</span>
                      </div>
                    )}
                  </motion.div>
                ) : pingResult?.success === false && pingResult.error !== 'All ping attempts failed' ? (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-center gap-2 flex-wrap"
                  >
                    <motion.span
                      className="text-sm text-red-600"
                      title={getPingErrorTranslation(pingResult.error)}
                    >
                      {t('proxies.card.pingFailed')}: {getPingErrorTranslation(pingResult.error)}
                    </motion.span>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            
            <motion.button
              onClick={handlePing}
              disabled={pinging || !localProxy.isOnline}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                pinging || !localProxy.isOnline
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100 active:bg-blue-200'
              }`}
              whileHover={pinging || !localProxy.isOnline ? {} : { scale: 1.05 }}
              whileTap={pinging || !localProxy.isOnline ? {} : { scale: 0.95 }}
            >
              {pinging ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  {t('proxies.card.pinging')} {pingProgress.current > 0 && `(${pingProgress.current}/${pingProgress.total})`}
                </span>
              ) : (
                t('proxies.card.pingButton')
              )}
            </motion.button>
          </div>
        </div>

        {/* Статус и клиенты */}
        <div className="mt-2 space-y-1">
          <HighlightedValue fieldName="isOnline" value={localProxy.isOnline}>
            <p className="text-xs text-gray-500">
              {localProxy.isOnline || localProxy.status === 'online'
                ? t('proxies.card.available')
                : t('proxies.card.unavailable')}
            </p>
          </HighlightedValue>
          
          {localProxy.connectedClients !== undefined && (
            <HighlightedValue fieldName="connectedClients" value={localProxy.connectedClients}>
              <p className="text-xs text-gray-500">
                {t('proxies.card.connectedClients')}: <span className="font-medium text-gray-700">{localProxy.connectedClients}</span>
              </p>
            </HighlightedValue>
          )}
        </div>
        
      </Card>
    </motion.div>
  );
};

/**
 * Функция сравнения для React.memo
 * Предотвращает лишние ререндеры при стабильных пропсах
 */
const propsAreEqual = (prevProps, nextProps) => {
  // Быстрые проверки
  if (prevProps.index !== nextProps.index) return false;
  
  const prevProxy = prevProps.proxy;
  const nextProxy = nextProps.proxy;
  
  // Если прокси нет или сменился ID — нужен ререндер
  if (!prevProxy || !nextProxy) return prevProxy === nextProxy;
  if (prevProxy.proxyId !== nextProxy.proxyId) return false;
  
  // Проверяем критические поля для ререндера
  const criticalFields = ['status', 'isOnline', 'lastHeartbeat', 'connectedClients'];
  for (const field of criticalFields) {
    if (prevProxy[field] !== nextProxy[field]) return false;
  }
  
  // Сравниваем changedFields (массивы строк)
  if (!compareChangedFields(prevProps.changedFields, nextProps.changedFields)) {
    return false;
  }
  
  // Update-объект (обычно ephemeral, можно сравнивать по ссылке или игнорировать)
  if (prevProps.update !== nextProps.update) return false;
  
  return true;
};

export default memo(ProxyCard, propsAreEqual);